import { ReactElement, useMemo, useState } from 'react'
import { I18n } from 'src/i18n'
import styled from 'styled-components'
import Wizard from '../../../../Wizard'
import {
  AddToListStage,
  useAddToListStore,
} from 'src/store/lists/AddToListStore'
import { defaultTheme } from 'src/theme'
import { useListsStore } from 'src/store/lists/ListsStore'
import Breadcrumb from '../../../../Breadcrumb/Breadcrumb'
import SelectFile from './SelectFile'
import FileSelected from './FileSelected'
import ErrorMessage from '../Errors/ValidationErrorMsg'
import { observer } from 'mobx-react-lite'

const ImportFile = observer((): ReactElement => {
  const addToListStore = useAddToListStore()
  const listStore = useListsStore()
  const categoryLabel = listStore.getSelectedCategoryLabel()
  const listLabel = listStore.getSelectedListLabel()
  const listId = listStore.getSelectedListId()
  const [file, setFile] = useState<File>(null)
  const error = addToListStore.getError()

  const goNext = (): void => {
    if (!file) return

    const validate = async () => await addToListStore.validateFile(listId, file)

    addToListStore.runValidation(validate)
  }

  const goBackLink = {
    text: 'back',
    action: () => {
      addToListStore.setError(null)
      addToListStore.previousStage()
    },
  }

  const goNextLink = { text: 'next', action: goNext }

  const breadCrumbItems = useMemo(
    () => [{ text: categoryLabel }, { text: listLabel }],
    [categoryLabel, listLabel]
  )

  const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    clearError()
    const filesArray = Array.from(e.target.files)
    const uploadFile = filesArray[0]

    if (uploadFile.size > 0) {
      setFile(uploadFile)
    }
  }

  const clearError = () => {
    addToListStore.setError(null)
  }

  const disableNextButton =
    addToListStore.getStage() === AddToListStage.ADD_PROMPT && error !== null

  return (
    <Wizard
      goNext={goNextLink}
      goBack={goBackLink}
      disableNextButton={disableNextButton}
    >
      <Wrapper>
        <Breadcrumb items={breadCrumbItems} />
        <Instructions>
          <I18n name="importMultipleItemsFromCSVFile" />
          {'. '}
          <I18n name="theFileShouldIncludeColumnsWithHeadingsForGroupMFRPartNumberStockingQtyandComments" />
          <StyledAnchor href="/csv/LIST_TEMPLATE.csv">
            <I18n name="useOurTemplateListFile" />
          </StyledAnchor>
        </Instructions>

        <Container>
          {error && <ErrorMessage onClose={clearError} error={error} />}
          {!file && <SelectFile onSelect={handleSelectFile} />}
          {file && <FileSelected file={file} setFile={setFile} />}
        </Container>
      </Wrapper>
    </Wizard>
  )
})

const Wrapper = styled.div`
  padding: 0px 0px 80px 0px;
`

const Instructions = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
`

const Container = styled.div`
  padding: 32px 0px 0px 0px;
`

const StyledAnchor = styled.a`
  color: ${defaultTheme.colors.link};
  margin-left: 0.5rem;
  text-decoration: none;
`

export default ImportFile
